// this (wdyr) MUST be loaded first in order for it to do it's magic
// import './wdyr';
import './index.css';
// Import the services via the index file in that folder
import './services';

import { StrictMode } from 'react';
import { createRoot, type Root } from 'react-dom/client';
import { Provider } from 'react-redux';
import { LoadingModal } from '@adc/parallax-component-library';

import {
  InitializationErrorPage,
  MainContainer,
  Providers,
} from '~/components/utility';
import { setUrls } from '~/utilities/dataHelpers';
import { _config, getEnvConfig } from '~/utilities/envConfig';
import * as log from '~/utilities/log';
import mediator from '~/utilities/mediator';
import { createStore } from '~/utilities/store';

import App from './App';

// if (import.meta.env.DEV {
//   import('./dev');
//   App.whyDidYouRender = true;
// }

let store: ReturnType<typeof createStore>;
let root: Root;

// This is the starting point of our app launch life-cycle
const init = () => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  root = createRoot(document.getElementById('root')!);

  store = createStore();

  renderLoading();

  getEnvConfig()
    .then((config) => {
      log.init(config.logLevel);

      if (config.mock) {
        mediator.once('MOCK:setup:initialized', () => {
          initializeStore(config);
        });
        // This import would allow you to have the mock code ready to go
        import('./services/mock')
          .then(({ startMockWorker }) => {
            void startMockWorker();
          })
          .catch((err: unknown) => {
            log.error('failed to start mock worked', err);
          });
      } else {
        initializeStore(config);
      }
    })
    .catch((err: unknown) => {
      log.error('failed to get the environment config:', err);
      renderError();
    });
};

const renderLoading = () => {
  root.render(
    <StrictMode>
      <Providers>
        <MainContainer>
          <LoadingModal aria-label="Loading" />
        </MainContainer>
      </Providers>
    </StrictMode>,
  );
};

// Renders our UnexpectedError display and minimally bootstraps i18n
const renderError = () => {
  // since there was an error, need to initialize services anyway
  mediator.publish('init:i18n');

  root.render(
    <StrictMode>
      <InitializationErrorPage />
    </StrictMode>,
  );
};

// Finally render our React trees
const renderApp = () => {
  try {
    root.render(
      <StrictMode>
        <Provider store={store}>
          <App />
        </Provider>
      </StrictMode>,
    );
  } catch (e) {
    log.error(e);
    renderError();
  }
};

// Add code here that you would like to do when you have the env config ready
// This is likely going to be lagic to attempt getting an existing session

const initializeStore = (envConfig: typeof _config) => {
  setUrls(envConfig.api);

  store = createStore({ config: envConfig });

  mediator.publish('store:created');
};

// Begin
init();

// Services that need to publish that they are ready
const appReady = {
  i18n: false,
  router: false,

  // state would be useful to track if redux store is ready
  // state: false,
};

// Listens for all services to be complete so that we have everything we need when we mount React
const readySub = mediator.subscribe(
  'app:ready',
  (ready: Partial<typeof appReady>) => {
    Object.assign(appReady, ready);

    const done = Object.values(appReady).every(Boolean);

    // i18n sets the document title, so we tell our router to 'refresh' to get the
    // document title to update on Android
    if (ready.i18n) {
      mediator.publish('init:router');
    }

    if (done) {
      mediator.publish('app:mounted');
      mediator.remove('app:ready', readySub.id);
    }
  },
);

// This block will only be called when the page is fully ready
// right now that means that our router service has spun up and done its initial work (redirecting if necessary)
// and the nav service has updated the store to reflect that.
// THEN 'app:ready' is published. otherwise the components may erroneously read the location information
// from the store (for example, the component may try reading it before the nav service has updated it, this
// was a problem fairly consistent with IE)
mediator.once('app:mounted', renderApp);
